

















































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { get as _get } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import Container from '@/components/Container.vue';
import FHIRConfirmModal from './components/FHIRConfirmModal.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { isUserAllowed } from '@/utils/rbac.util';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { PermissionsMatrixActionsEnum } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import EditFhirClientSidebar from './components/EditFhirClientSidebar.vue';
import FHIRApiAccessTab from './components/FHIRApiAccessTab.vue';
import {
  FHIRClient,
  UpdateFHIRClientPayload
} from './types/admin-fhir-access-management.types';

@Component({
  computed: {
    PermissionsMatrixActionsEnum() {
      return PermissionsMatrixActionsEnum;
    }
  },
  methods: { isUserAllowed },
  components: {
    Container,
    BaseLoading,
    EditFhirClientSidebar,
    FHIRApiAccessTab
  }
})
export default class FHIRClientDetail extends Vue {
  public activeTab = 0;
  public showFullKey = false;
  public copied = false;
  public isEditSidebarOpen = false;

  @Action('fhir/getFhirClientById')
  getFhirClientById!: (id: number) => void;

  @Action('fhir/updateFhirClient')
  updateFhirClient!: (args: {
    id: number;
    payload: UpdateFHIRClientPayload;
  }) => void;

  @Action('fhir/deleteFhirClient')
  deleteFhirClient!: (id: number) => void;

  @State(({ fhir }: RootState) => fhir.fhirClient)
  public client!: FHIRClient | undefined;

  @State(({ fhir }: RootState) => fhir.apiState.getFhirClientById)
  public getFhirClientByIdState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.updateFhirClient)
  public updateFhirClientState!: ApiState;

  @State(({ fhir }: RootState) => fhir.apiState.deleteFhirClient)
  public deleteFhirClientState!: ApiState;

  private pendingAction:
    | 'edit'
    | 'replaceKey'
    | 'toggleStatus'
    | 'delete'
    | undefined;

  get loading() {
    return this.getFhirClientByIdState?.loading;
  }

  public mounted() {
    const id = Number(this.$route.params.id);
    if (id) {
      this.getFhirClientById(id);
    }
  }

  public onEdit() {
    if (!this.client) return;
    this.isEditSidebarOpen = true;
  }

  public onSaveEdit(payload: UpdateFHIRClientPayload) {
    if (!this.client) return;
    this.pendingAction = payload.publicKey ? 'replaceKey' : 'edit';
    this.updateFhirClient({ id: this.client.id, payload });
  }

  public onToggleStatus() {
    if (!this.client) return;
    const turningActive = !this.client.isActive;
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: turningActive ? 'Activate Client?' : 'Deactivate Client?',
        modalContent: turningActive
          ? 'This client will regain access to the FHIR API.'
          : 'This client will lose access to the FHIR API until reactivated.',
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.pendingAction = 'toggleStatus';
          this.updateFhirClient({
            id: this.client!.id,
            payload: { isActive: turningActive }
          });
        }
      }
    });
  }

  public onDelete() {
    if (!this.client) return;
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Client?',
        modalContent:
          'This will permanently delete the client, including all credentials and access configurations. API access will be revoked immediately.',
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.pendingAction = 'delete';
          this.deleteFhirClient(this.client!.id);
        }
      }
    });
  }

  public async copyKey() {
    if (!this.client?.publicKey) return;
    await navigator.clipboard.writeText(this.client.publicKey);
    this.copied = true;
    setTimeout(() => (this.copied = false), 2000);
  }

  @Watch('updateFhirClientState.success')
  @isTruthy
  public onUpdateSuccess() {
    const messages: Record<string, string> = {
      edit: 'Client updated successfully.',
      replaceKey: 'SSH Public Key Successfully Replaced',
      toggleStatus: 'Client status updated successfully.'
    };
    Toast.open({
      message: messages[this.pendingAction ?? 'edit'],
      position: 'is-top',
      type: 'is-success'
    });
    this.pendingAction = undefined;
  }

  @Watch('updateFhirClientState.error')
  @isTruthy
  public onUpdateError() {
    Toast.open({
      message: _get(
        this.updateFhirClientState.error,
        'response.data.message',
        'Failed to update FHIR client.'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
    this.pendingAction = undefined;
  }

  @Watch('deleteFhirClientState.success')
  @isTruthy
  public onDeleteSuccess() {
    Toast.open({
      message: 'Client deleted successfully.',
      position: 'is-top',
      type: 'is-success'
    });
    this.$router.push({
      name: 'third-party-management',
      query: { tab: 'FHIR APIs' }
    });
  }

  @Watch('deleteFhirClientState.error')
  @isTruthy
  public onDeleteError() {
    Toast.open({
      message: _get(
        this.deleteFhirClientState.error,
        'response.data.message',
        'Failed to delete FHIR client.'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
  }
}
